import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CheckCircle, Circle, ArrowRight, User, Buildings, Files, Calculator, FileText, PaperPlaneTilt, Spinner, X, Car, UploadSimple, Sparkle, FilePdf, CaretDown, Plus, Trash, PencilSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useActivity } from "@/context/ActivityContext";
import PdfViewer from "@/components/PdfViewer";
import A4Sheet from "@/components/A4Sheet";
import FitA4 from "@/components/FitA4";
import SchriftsatzEditor, { WordButton } from "@/components/SchriftsatzEditor";

const SCHADENART_META = {
  reparaturschaden: { label: "Reparaturschaden", cls: "bg-[#16A34A]/10 text-[#15803D] border-[#16A34A]/30" },
  "130_prozent": { label: "130 %-Fall", cls: "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30" },
  totalschaden: { label: "Wirtschaftlicher Totalschaden", cls: "bg-[#D32F2F]/10 text-[#B91C1C] border-[#D32F2F]/30" },
};
const SCHADENART_QUELLE = { gutachten: "laut Gutachten", manuell: "manuell festgelegt", berechnet: "automatisch ermittelt" };

// Sidebar listing structured capture steps. Each step knows whether the akte already
// fulfills it. Clicking jumps to the editor of that step (inline panel below sidebar).

export default function CaptureSteps({ akte, docs, schriftsaetze, onUpdated, onCloseCapture }) {
  const [activeStep, setActiveStep] = useState(null);
  const isOWi = akte?.art === "owi";

  const steps = [
    {
      key: "mandant",
      label: "Mandant",
      icon: User,
      done: (!!akte?.mandant_id || !!akte?.mandant_name) && docs.length > 0,
      hint: akte?.mandant_name
        ? `${akte.mandant_name}${docs.length ? ` · ${docs.length} Dok.` : ""}`
        : (isOWi ? "Mandant + Bußgeldbescheid hochladen" : "Mandant + Gutachten hochladen"),
    },
    {
      key: "gegner",
      label: isOWi ? "Behörde / Bußgeldstelle" : "Gegner / Versicherung",
      icon: Buildings,
      done: !!(akte?.gegner?.name || akte?.gegner?.versicherung),
      hint: akte?.gegner?.name || akte?.gegner?.versicherung || (isOWi ? "Bußgeldstelle erfassen" : "Unfallgegner & Versicherung"),
    },
    {
      key: "fahrzeug",
      label: isOWi ? "Vorgang (Tatort/Datum)" : "Fahrzeug / Unfall",
      icon: Car,
      done: !!(akte?.fahrzeug?.kennzeichen || akte?.unfalldatum || akte?.unfallort),
      hint: [akte?.fahrzeug?.kennzeichen, akte?.unfallort, akte?.unfalldatum && new Date(akte.unfalldatum).toLocaleDateString("de-DE")].filter(Boolean).join(" · ") || "Fahrzeug, Datum, Ort",
    },
    !isOWi && {
      key: "bezifferung",
      label: "Bezifferung",
      icon: Calculator,
      done: !!(akte?.bezifferung && (akte.bezifferung.streitwert > 0 || akte.bezifferung.reparaturkosten > 0)),
      hint: akte?.bezifferung?.streitwert
        ? [new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(akte.bezifferung.streitwert), SCHADENART_META[akte.bezifferung.schadenart]?.label].filter(Boolean).join(" · ")
        : "Schadensumme berechnen",
    },
    {
      key: "schriftsatz",
      label: isOWi ? "Einspruch" : "Anspruchsschreiben",
      icon: FileText,
      done: schriftsaetze.some(s => s.typ === (isOWi ? "owi_einspruch" : "anspruchsschreiben")),
      hint: schriftsaetze.find(s => s.typ === (isOWi ? "owi_einspruch" : "anspruchsschreiben"))
        ? "Vorbereitet"
        : "Wird automatisch generiert",
    },
    !isOWi && {
      key: "klageschrift",
      label: "Klageschrift",
      icon: FileText,
      done: schriftsaetze.some(s => s.typ === "klageschrift"),
      hint: schriftsaetze.some(s => s.typ === "klageschrift") ? "Vorbereitet" : "Wird automatisch generiert",
    },
    !isOWi && {
      key: "rvg",
      label: "Honorar (RVG)",
      icon: Calculator,
      done: !!(akte?.bezifferung?.rvg_gebuehr && akte.bezifferung.rvg_gebuehr > 0),
      hint: akte?.bezifferung?.rvg_gebuehr
        ? new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(akte.bezifferung.rvg_gebuehr)
        : "RVG-Gebühr berechnen",
    },
    {
      key: "versand",
      label: "Versand",
      icon: PaperPlaneTilt,
      done: schriftsaetze.some(s => s.versendet_am),
      hint: schriftsaetze.some(s => s.versendet_am) ? "Versendet" : "Schreiben absenden",
    },
  ].filter(Boolean);

  const completed = steps.filter(s => s.done).length;
  const progress = Math.round((completed / steps.length) * 100);

  // Auto-jump to first unfinished on mount
  useEffect(() => {
    if (activeStep == null) {
      const firstOpen = steps.find(s => !s.done);
      if (firstOpen) setActiveStep(firstOpen.key);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8" data-testid="capture-steps">
      {/* SIDEBAR */}
      <aside className="lg:sticky lg:top-8 lg:self-start space-y-4">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="overline text-[#D32F2F]">Erfassungsschritte</p>
            {onCloseCapture && (
              <button
                onClick={onCloseCapture}
                className="text-[#9CA3AF] hover:text-[#0A192F]"
                title="Erfassungs-Modus beenden"
                data-testid="capture-close"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="font-serif text-3xl tracking-tight">{completed}</span>
            <span className="text-sm text-[#6B7280]">/ {steps.length} erledigt</span>
          </div>
          <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#1A2238] transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="capture-progress-bar"
            />
          </div>
          <p className="text-xs text-[#9CA3AF] mt-2">{progress}% — füllen Sie die Schritte der Reihe nach.</p>
        </div>

        <nav className="space-y-1">
          {steps.map((s) => {
            const StepIcon = s.icon;
            const active = activeStep === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setActiveStep(s.key)}
                data-testid={`capture-step-${s.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors ${
                  active
                    ? "bg-[#1A2238] text-white"
                    : s.done
                    ? "text-[#0A192F] hover:bg-[#F3F4F6]"
                    : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A192F]"
                }`}
              >
                <span className={`relative h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                  active
                    ? "bg-white/15 text-white"
                    : s.done
                    ? "bg-[#16A34A]/10 text-[#16A34A]"
                    : "bg-[#F3F4F6] text-[#1A2238]"
                }`}>
                  <StepIcon size={18} weight="duotone" />
                  {s.done && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-[#16A34A] text-white flex items-center justify-center ring-2 ring-white">
                      <CheckCircle size={12} weight="fill" />
                    </span>
                  )}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium">{s.label}</span>
                  <span className={`block text-[11px] truncate ${active ? "text-white/70" : "text-[#9CA3AF]"}`}>
                    {s.hint}
                  </span>
                </span>
                <ArrowRight size={14} className={active ? "opacity-80" : "opacity-30"} />
              </button>
            );
          })}
        </nav>
      </aside>

      {/* CONTENT panel for active step */}
      <div className="min-w-0">
        {activeStep === "mandant" && (
          <MandantPanel
            akte={akte}
            docs={docs}
            onSaved={onUpdated}
            onNext={() => setActiveStep("gegner")}
          />
        )}
        {activeStep === "gegner" && <GegnerPanel akte={akte} docs={docs} onSaved={onUpdated} onNext={() => setActiveStep("fahrzeug")} />}
        {activeStep === "fahrzeug" && <FahrzeugPanel akte={akte} docs={docs} onSaved={onUpdated} onNext={() => setActiveStep(isOWi ? "schriftsatz" : "bezifferung")} />}
        {activeStep === "bezifferung" && (
          <BezifferungInlinePanel akte={akte} docs={docs} schriftsaetze={schriftsaetze} onSaved={onUpdated} onNext={() => setActiveStep("schriftsatz")} />
        )}
        {activeStep === "schriftsatz" && (
          <SchriftsatzInlinePanel
            akte={akte}
            schriftsaetze={schriftsaetze}
            typ={isOWi ? "owi_einspruch" : "anspruchsschreiben"}
            title={isOWi ? "Einspruch" : "Anspruchsschreiben"}
            onSaved={onUpdated}
            onNext={() => setActiveStep(isOWi ? "versand" : "klageschrift")}
          />
        )}
        {activeStep === "klageschrift" && (
          <SchriftsatzInlinePanel
            akte={akte}
            schriftsaetze={schriftsaetze}
            typ="klageschrift"
            title="Klageschrift"
            onSaved={onUpdated}
            onNext={() => setActiveStep("rvg")}
          />
        )}
        {activeStep === "rvg" && (
          <RvgPanel akte={akte} schriftsaetze={schriftsaetze} onSaved={onUpdated} onNext={() => setActiveStep("versand")} />
        )}
        {activeStep === "versand" && (
          <VersandInlinePanel akte={akte} schriftsaetze={schriftsaetze} onSaved={onUpdated} />
        )}
      </div>
    </div>
  );
}

// ----- Inline panels for capturing the structured fields -----

/**
 * AiInput / AiTextarea — zeigt blaues Highlight + Sparkle-Icon wenn das Feld aus KI-Extraktion stammt.
 * `aiFilled` = true wenn der aktuelle Wert von der KI gesetzt wurde (per akte.applied_to_akte oder doc.extracted_data).
 */
function aiFilledClass(aiFilled) {
  return aiFilled
    ? "border-[#3B82F6] bg-[#EFF6FF] focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
    : "";
}

function AiBadge() {
  return (
    <Sparkle
      size={11}
      weight="fill"
      className="ml-1.5 text-[#3B82F6]"
      title="Aus Gutachten extrahiert"
    />
  );
}

function PanelShell({ title, hint, onSubmit, saving, children, onSkip }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-5">
      <div>
        <h2 className="font-serif text-2xl">{title}</h2>
        {hint && <p className="text-sm text-[#6B7280] mt-1">{hint}</p>}
      </div>
      <form onSubmit={onSubmit} className="space-y-4">
        {children}
        <div className="flex justify-end gap-2 pt-2">
          {onSkip && (
            <Button type="button" variant="ghost" onClick={onSkip} data-testid="capture-skip">Überspringen</Button>
          )}
          <Button type="submit" disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="capture-save">
            {saving ? <Spinner size={14} className="mr-2 animate-spin" /> : null}
            Speichern &amp; weiter
          </Button>
        </div>
      </form>
    </div>
  );
}

function MandantPanel({ akte, docs, onSaved, onNext }) {
  const { user } = useAuth();
  const { track } = useActivity();
  const isOWi = akte?.art === "owi";
  const [mandanten, setMandanten] = useState([]);
  const [tab, setTab] = useState("mandant"); // mandant | vollmacht
  const [mode, setMode] = useState(akte?.mandant_id ? "select" : "new");
  const [mandantId, setMandantId] = useState(akte?.mandant_id || "");
  const [mandant, setMandant] = useState(null);
  const [form, setForm] = useState({ vorname: "", nachname: "", email: "", telefon: "", strasse: "", plz: "", ort: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [previewDok, setPreviewDok] = useState(null);
  const [docDropdownOpen, setDocDropdownOpen] = useState(false);

  // KI-Werte aus dem ersten analysierten Gutachten — für blaues Highlighting.
  // Werte aus dem Gutachten werden hier "Wahrheits-Quelle" — wenn Form-Wert gleich KI-Wert,
  // dann ist das Feld "AI-filled".
  const aiData = (() => {
    const analyzed = (docs || []).find(d => d.analyzed && d.extracted_data);
    return analyzed?.extracted_data || null;
  })();
  const aiNameParts = (() => {
    if (!aiData?.geschaedigter_name) return { vorname: "", nachname: "" };
    const parts = aiData.geschaedigter_name.trim().split(" ");
    return parts.length > 1
      ? { vorname: parts.slice(0, -1).join(" "), nachname: parts[parts.length - 1] }
      : { vorname: "", nachname: parts[0] || "" };
  })();
  const aiAddr = (() => {
    if (!aiData?.geschaedigter_anschrift) return { strasse: "", plz: "", ort: "" };
    const m = aiData.geschaedigter_anschrift.match(/\b(\d{5})\s+([A-Za-zÄÖÜäöüß\.\- ]+)/);
    const plz = m?.[1] || "";
    const ort = (m?.[2] || "").trim().replace(/[,.;]+$/, "").split(",")[0].trim();
    let strasse = aiData.geschaedigter_anschrift;
    if (m) strasse = (strasse.slice(0, m.index) + strasse.slice(m.index + m[0].length)).replace(/[,;]+/g, " ").trim();
    return { strasse: strasse.slice(0, 80), plz, ort };
  })();
  const isAi = (key) => {
    if (!aiData) return false;
    if (key === "vorname") return !!aiNameParts.vorname && form.vorname === aiNameParts.vorname;
    if (key === "nachname") return !!aiNameParts.nachname && form.nachname === aiNameParts.nachname;
    if (key === "strasse") return !!aiAddr.strasse && form.strasse === aiAddr.strasse;
    if (key === "plz") return !!aiAddr.plz && form.plz === aiAddr.plz;
    if (key === "ort") return !!aiAddr.ort && form.ort === aiAddr.ort;
    if (key === "telefon") return !!aiData.geschaedigter_telefon && form.telefon === aiData.geschaedigter_telefon;
    if (key === "email") return !!aiData.geschaedigter_email && form.email === aiData.geschaedigter_email;
    return false;
  };

  useEffect(() => {
    api.get("/mandanten").then(r => setMandanten(r.data)).catch(() => {});
  }, []);

  // Tab list: Mandant (kombiniert mit Gutachten) + Vollmacht
  const tabs = [
    { k: "mandant", label: isOWi ? "Mandant & Bußgeld" : "Mandant & Gutachten" },
    { k: "vollmacht", label: "Vollmacht", check: !!mandant?.vollmacht_unterschrift },
  ];

  // Wenn Akte einen mandant_id hat → zugehörigen Mandant laden, Felder befüllen.
  // WICHTIG: Auch bei Updates der Akte (KI-Auto-Create) → mandantId nachsynchronisieren.
  useEffect(() => {
    console.log("[MandantPanel] akte.mandant_id changed:", akte?.mandant_id, "current mandantId:", mandantId);
    if (akte?.mandant_id && akte.mandant_id !== mandantId) {
      console.log("[MandantPanel] -> setMandantId");
      setMandantId(akte.mandant_id);
      setMode("select");
    }
    // eslint-disable-next-line
  }, [akte?.mandant_id]);

  // Mandant-Daten laden + Form synchronisieren.
  // Triggert wenn: mandantId wechselt ODER akte.updated_at wechselt (für Live-Refresh nach Scan).
  useEffect(() => {
    console.log("[MandantPanel] sync effect — mandantId:", mandantId, "akte.updated_at:", akte?.updated_at);
    if (mandantId) {
      api.get(`/mandanten/${mandantId}`).then(r => {
        console.log("[MandantPanel] Mandant loaded:", r.data.vorname, r.data.nachname);
        setMandant(r.data);
        setForm(f => {
          const next = {
            vorname: f.vorname || r.data.vorname || "",
            nachname: f.nachname || r.data.nachname || "",
            email: f.email || r.data.email || "",
            telefon: f.telefon || r.data.telefon || "",
            strasse: f.strasse || r.data.strasse || "",
            plz: f.plz || r.data.plz || "",
            ort: f.ort || r.data.ort || "",
          };
          console.log("[MandantPanel] setForm:", next);
          return next;
        });
      }).catch((e) => {
        console.error("[MandantPanel] Mandant fetch failed:", e);
      });
    } else if (akte?.mandant_name && !form.vorname && !form.nachname) {
      const parts = akte.mandant_name.trim().split(" ");
      setForm(f => ({
        ...f,
        vorname: parts.length > 1 ? parts.slice(0, -1).join(" ") : "",
        nachname: parts.length > 1 ? parts[parts.length - 1] : parts[0] || "",
      }));
    }
    // eslint-disable-next-line
  }, [mandantId, akte?.mandant_name, akte?.updated_at]);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      let resolvedId = mandantId;
      let resolvedName = "";
      if (mode === "new" && form.vorname && form.nachname) {
        const r = await api.post("/mandanten", form);
        resolvedId = r.data.id;
        setMandant(r.data);
        resolvedName = `${r.data.vorname} ${r.data.nachname}`.trim();
      } else if (mode === "select" && mandantId) {
        // Patch existing mandant with form changes (so corrections persist)
        const r = await api.patch(`/mandanten/${mandantId}`, form);
        setMandant(r.data);
        resolvedName = `${r.data.vorname} ${r.data.nachname}`.trim();
      } else {
        toast.error("Vorname und Nachname sind Pflicht");
        setSaving(false);
        return;
      }
      await api.patch(`/akten/${akte.id}`, { mandant_id: resolvedId, mandant_name: resolvedName });
      toast.success("Mandant gespeichert");
      onSaved?.();
      if (e) onNext?.();
    } catch (err) {
      toast.error(errMsg(err, "Mandant konnte nicht gespeichert werden"));
    } finally { setSaving(false); }
  };

  const uploadDocs = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const stopUpload = track(`Lade ${files.length} Dokument(e) hoch …`);
    try {
      // Parallel hochladen
      const uploadResults = await Promise.allSettled(
        files.map(f => {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("akte_id", akte.id);
          fd.append("kategorie", isOWi ? "bussgeld" : "gutachten");
          return api.post("/dokumente/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        })
      );
      const uploadedDocs = uploadResults
        .filter(r => r.status === "fulfilled")
        .map(r => r.value.data);
      const failedCount = uploadResults.length - uploadedDocs.length;
      stopUpload();
      if (uploadedDocs.length === 0) {
        const reason = uploadResults.find(r => r.status === "rejected")?.reason;
        toast.error(errMsg(reason, "Upload fehlgeschlagen"), { duration: 8000 });
        return;
      }
      if (failedCount > 0) {
        const reason = uploadResults.find(r => r.status === "rejected")?.reason;
        toast.warning(`${uploadedDocs.length}/${files.length} hochgeladen — ${errMsg(reason, `${failedCount} fehlgeschlagen`)}`, { duration: 8000 });
      }

      // Stufe 1: Akte-Daten eintragen (Mandant, Gegner, Fahrzeug, Bezifferung)
      const stopExtract = track("Akte wird automatisch ausgefüllt (Mandant, Gegner, Fahrzeug, Bezifferung) …");

      // PARALLEL zum analyze-Call: Polling auf akte.updated_at. Sobald das Backend
      // _apply_extraction_to_akte ausgeführt hat (≈ 7-10 s), aktualisieren wir die UI
      // — egal wann die analyze-Response durch den Cluster-Ingress zurückkommt.
      const oldUpdatedAt = akte?.updated_at;
      let pollAbort = false;
      const pollPromise = (async () => {
        for (let i = 0; i < 40 && !pollAbort; i++) {  // bis zu ~60 s
          await new Promise(r => setTimeout(r, 1500));
          try {
            const r = await api.get(`/akten/${akte.id}`);
            if (!oldUpdatedAt || r.data.updated_at !== oldUpdatedAt) {
              onSaved?.();
              return true;
            }
          } catch { /* keep polling */ }
        }
        return false;
      })();

      let scheduled = false;
      const pendingPolls = [];
      const errors = [];
      const analyzeResults = await Promise.allSettled(
        uploadedDocs.map(d => api.post(`/dokumente/${d.id}/analyze`))
      );
      for (let i = 0; i < analyzeResults.length; i++) {
        const r = analyzeResults[i];
        const dokId = uploadedDocs[i].id;
        if (r.status === "fulfilled") {
          const data = r.value?.data || {};
          if (data.status === "pending" || data.status === "in_progress") pendingPolls.push(dokId);
          else if (data.status === "failed") errors.push(data.detail || "Analyse fehlgeschlagen");
          else if (data.auto_drafts_scheduled) scheduled = true;
          continue;
        }
        // Verbindung abgebrochen (Gateway-Timeout 502/503/504, Netzwerk) → Backend arbeitet weiter → Status pollen
        const st = r.reason?.response?.status;
        if (!st || st >= 502) { pendingPolls.push(dokId); continue; }
        // Sonst prüfen, ob die Analyse trotzdem durchgelaufen ist — kein falscher Fehler-Toast
        try {
          const sr = await api.get(`/dokumente/${dokId}/status`);
          if (sr.data.analyzed) { scheduled = true; continue; }
          if (sr.data.analysis_status === "analyzing") { pendingPolls.push(dokId); continue; }
          errors.push(sr.data.analysis_error || errMsg(r.reason, "Analyse fehlgeschlagen"));
        } catch {
          errors.push(errMsg(r.reason, "Analyse fehlgeschlagen"));
        }
      }
      stopExtract();
      pollAbort = true;  // wenn analyze schon zurück ist, müssen wir nicht weiter pollen

      if (errors.length) {
        toast.error(errors[0], { duration: 10000 });
      }
      // Wenn das parallele Polling noch nicht gefeuert hat, jetzt manuell refreshen
      const polled = await pollPromise;
      if (!polled) onSaved?.();

      // Stufe 2: Bezifferung + Anspruchsschreiben + Klageschrift im Hintergrund —
      // werden vom Backend via _auto_generate_drafts angestoßen; wir tracken nur die Pill
      let stopDrafts = null;
      if (scheduled) {
        stopDrafts = track("Schriftsätze werden vorbereitet (Anspruchsschreiben, Klageschrift, RVG-Honorar)");
        // Stop nach max. 3 Min. — Frontend wartet nicht aktiv, Anwalt kann weiterarbeiten
        setTimeout(() => {
          stopDrafts?.();
          onSaved?.(); // letzter Refresh, Schriftsätze sollten dann sichtbar sein
        }, 180000);
      }// Polling-Fallback für noch unfertige Analysen (502/Pending)
      if (pendingPolls.length > 0) {
        const stopPoll = track(`Analysiert noch ${pendingPolls.length} Dokument(e) …`);
        const pending = new Set(pendingPolls);
        let polls = 0;
        const maxPolls = 60;
        const interval = setInterval(async () => {
          polls++;
          for (const did of Array.from(pending)) {
            try {
              const sr = await api.get(`/dokumente/${did}/status`);
              if (sr.data.analyzed) {
                pending.delete(did);
                onSaved?.();
              } else if (sr.data.analysis_status === "failed") {
                pending.delete(did);
                toast.error(sr.data.analysis_error || "Analyse fehlgeschlagen", { duration: 10000 });
              }
            } catch { /* keep polling */ }
          }
          if (pending.size === 0 || polls >= maxPolls) {
            clearInterval(interval);
            stopPoll();
            if (polls >= maxPolls && pending.size > 0) {
              toast.warning("Analyse dauert ungewöhnlich lange — bitte Seite später aktualisieren");
            }
          }
        }, 3000);
      }
    } catch (err) {
      stopUpload();
      toast.error(errMsg(err, "Fehler beim Hochladen"), { duration: 8000 });
    } finally {
      setUploading(false);
    }
  };

  const saveSignature = async (dataUrl) => {
    if (!mandantId) {
      toast.error("Bitte zuerst Mandant speichern");
      return;
    }
    try {
      await api.patch(`/mandanten/${mandantId}`, {
        vollmacht_unterschrift: dataUrl,
        vollmacht_unterzeichnet_am: new Date().toISOString(),
        vollmacht_ort: form.ort || "",
      });
      toast.success("Unterschrift gespeichert");
      const r = await api.get(`/mandanten/${mandantId}`);
      setMandant(r.data);
    } catch (err) {
      toast.error(errMsg(err, "Unterschrift konnte nicht gespeichert werden"));
    }
  };

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E5E7EB]">
        {tabs.map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
              tab === t.k ? "border-[#1A2238] text-[#1A2238]" : "border-transparent text-[#6B7280] hover:text-[#0A192F]"
            }`}
            data-testid={`mandant-tab-${t.k}`}
          >
            {t.label}
            {t.check && <CheckCircle size={14} weight="fill" className="text-[#16A34A]" />}
          </button>
        ))}
      </div>

      {tab === "mandant" && (
        <div className="space-y-4">
          {/* Mandant-Karte (volle Breite) */}
          <div className="space-y-4 min-w-0">
            <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="mandant-unified-card">
              {/* Scanner-Bar (kompakt, oben) — mit Dropdown der gescannten Dokumente */}
              <div
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-[#F3F4F6]"); }}
                onDragLeave={(e) => e.currentTarget.classList.remove("bg-[#F3F4F6]")}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("bg-[#F3F4F6]");
                  uploadDocs(Array.from(e.dataTransfer.files || []));
                }}
                className="px-6 py-4 border-b border-[#E5E7EB] bg-[#FAFAFA] transition-colors"
                data-testid="capture-mandant-drop"
              >
                <div className="flex flex-wrap items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-[#1A2238] text-white flex items-center justify-center shrink-0">
                    {uploading ? <Spinner size={18} className="animate-spin" /> : <Files size={18} weight="duotone" />}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-medium text-sm text-[#0A192F]">
                      {uploading
                        ? "Lädt hoch & analysiert …"
                        : isOWi ? "Bußgeldbescheid scannen" : "Gutachten scannen"}
                    </p>
                    <p className="text-xs text-[#6B7280]">
                      {docs.length === 0
                        ? "Datei ablegen — Akte, Bezifferung, Anspruchsschreiben & Honorar werden automatisch erstellt"
                        : `${docs.length} Dokument(e) · ${docs.filter(d => d.analyzed).length} analysiert`}
                    </p>
                  </div>
                  <label className="inline-block">
                    <input type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={(e) => uploadDocs(Array.from(e.target.files || []))} data-testid="capture-mandant-file-input" />
                    <span className="px-3 py-2 bg-[#1A2238] text-white rounded-md text-xs font-medium cursor-pointer hover:bg-[#2C3859] inline-flex items-center gap-1.5">
                      <UploadSimple size={14} weight="bold" /> Datei wählen
                    </span>
                  </label>
                </div>

                {/* Dropdown der gescannten Dokumente — direkt im Scanner-Feld */}
                {docs.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#E5E7EB]" data-testid="scanner-doc-dropdown">
                    <button
                      type="button"
                      onClick={() => setDocDropdownOpen(o => !o)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-white border border-[#E5E7EB] rounded text-sm text-[#0A192F] hover:border-[#1A2238] transition-colors"
                      data-testid="scanner-dropdown-toggle"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <FilePdf size={16} weight="duotone" className="text-[#D32F2F] shrink-0" />
                        <span className="truncate">
                          {previewDok ? previewDok.original_filename : `${docs.length} gescannte(s) Dokument(e) — auswählen`}
                        </span>
                      </span>
                      <CaretDown size={14} className={`transition-transform shrink-0 ${docDropdownOpen ? "rotate-180" : ""}`} />
                    </button>
                    {docDropdownOpen && (
                      <ul className="mt-1 bg-white border border-[#E5E7EB] rounded shadow-sm max-h-60 overflow-y-auto" data-testid="scanner-dropdown-list">
                        {docs.map(d => {
                          const isActive = previewDok?.id === d.id;
                          return (
                            <li key={d.id}>
                              <button
                                type="button"
                                onClick={() => { setPreviewDok(d); setDocDropdownOpen(false); }}
                                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                                  isActive ? "bg-[#1A2238] text-white" : "hover:bg-[#F3F4F6] text-[#0A192F]"
                                }`}
                                data-testid={`scanner-dropdown-item-${d.id}`}
                              >
                                <FilePdf size={16} weight="duotone" />
                                <span className="flex-1 truncate">{d.original_filename}</span>
                                {d.analyzed && (
                                  <span className={`text-[10px] uppercase tracking-wide ${isActive ? "text-white/80" : "text-[#16A34A]"}`}>
                                    analysiert
                                  </span>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Mandant-Formular */}
              <form onSubmit={save} className="p-6 space-y-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="overline text-[#D32F2F] mb-1">Mandant</p>
                    <h2 className="font-serif text-2xl">Wer wird vertreten?</h2>
                  </div>
                  <div className="flex gap-1.5">
                    <button type="button" onClick={() => setMode("new")} className={`px-3 py-1.5 rounded border text-xs ${mode === "new" ? "border-[#1A2238] bg-[#1A2238] text-white" : "border-[#E5E7EB] hover:border-[#1A2238]"}`} data-testid="mandant-mode-new">+ Neu</button>
                    <button type="button" onClick={() => setMode("select")} className={`px-3 py-1.5 rounded border text-xs ${mode === "select" ? "border-[#1A2238] bg-[#1A2238] text-white" : "border-[#E5E7EB] hover:border-[#1A2238]"}`} data-testid="mandant-mode-select">Aus Liste</button>
                  </div>
                </div>

                {mode === "select" && (
                  <div>
                    <Label>Mandant aus Liste</Label>
                    <Select value={mandantId} onValueChange={setMandantId}>
                      <SelectTrigger data-testid="capture-mandant-select"><SelectValue placeholder="auswählen …" /></SelectTrigger>
                      <SelectContent>
                        {mandanten.map(m => <SelectItem key={m.id} value={m.id}>{m.vorname} {m.nachname}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center">Vorname{isAi("vorname") && <AiBadge />}</Label>
                    <Input value={form.vorname} onChange={(e) => setForm({ ...form, vorname: e.target.value })} placeholder="z.B. Erika" required data-testid="capture-mandant-vorname" className={aiFilledClass(isAi("vorname"))} />
                  </div>
                  <div>
                    <Label className="flex items-center">Nachname{isAi("nachname") && <AiBadge />}</Label>
                    <Input value={form.nachname} onChange={(e) => setForm({ ...form, nachname: e.target.value })} placeholder="z.B. Mustermann" required data-testid="capture-mandant-nachname" className={aiFilledClass(isAi("nachname"))} />
                  </div>
                  <div>
                    <Label className="flex items-center">E-Mail{isAi("email") && <AiBadge />}</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="optional" data-testid="capture-mandant-email" className={aiFilledClass(isAi("email"))} />
                  </div>
                  <div>
                    <Label className="flex items-center">Telefon{isAi("telefon") && <AiBadge />}</Label>
                    <Input value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} placeholder="optional" data-testid="capture-mandant-telefon" className={aiFilledClass(isAi("telefon"))} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="flex items-center">Straße &amp; Hausnr.{isAi("strasse") && <AiBadge />}</Label>
                    <Input value={form.strasse} onChange={(e) => setForm({ ...form, strasse: e.target.value })} placeholder="z.B. Hohenzollernring 12" data-testid="capture-mandant-strasse" className={aiFilledClass(isAi("strasse"))} />
                  </div>
                  <div>
                    <Label className="flex items-center">PLZ{isAi("plz") && <AiBadge />}</Label>
                    <Input value={form.plz} onChange={(e) => setForm({ ...form, plz: e.target.value })} placeholder="50672" data-testid="capture-mandant-plz" className={aiFilledClass(isAi("plz"))} />
                  </div>
                  <div>
                    <Label className="flex items-center">Ort{isAi("ort") && <AiBadge />}</Label>
                    <Input value={form.ort} onChange={(e) => setForm({ ...form, ort: e.target.value })} placeholder="Köln" data-testid="capture-mandant-ort" className={aiFilledClass(isAi("ort"))} />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
                  {onNext && <Button type="button" variant="ghost" onClick={onNext} data-testid="capture-skip">Überspringen</Button>}
                  <Button type="submit" disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="capture-save">
                    {saving && <Spinner size={14} className="mr-2 animate-spin" />}
                    Speichern &amp; weiter
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* PDF-Viewer UNTER der Mandant-Karte (volle Breite) */}
          {previewDok && (
            <div
              className="min-w-0 min-h-[700px] flex flex-col"
              data-testid="mandant-pdf-split"
            >
              <PdfViewer
                mode="inline"
                dokumentId={previewDok.id}
                akteId={akte.id}
                dokumentName={previewDok.original_filename}
                onClose={() => setPreviewDok(null)}
                onMarkerChange={() => onSaved?.()}
              />
            </div>
          )}
        </div>
      )}

      {tab === "vollmacht" && (
        <VollmachtPanel mandant={mandant} mandantId={mandantId} onSave={saveSignature} akte={akte} user={user} />
      )}
    </div>
  );
}

function VollmachtPanel({ mandant, mandantId, onSave, akte, user }) {
  const canvasRef = React.useRef(null);
  const sheetRef = React.useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [text, setText] = useState("");
  const [savingText, setSavingText] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);

  // Logo aus Profil holen (authentifiziert)
  useEffect(() => {
    let active = true; let url;
    if (!user?.logo_path) { setLogoBlobUrl(null); return; }
    api.get("/auth/logo", { responseType: "blob" })
      .then(r => {
        if (!active) return;
        url = URL.createObjectURL(r.data);
        setLogoBlobUrl(url);
      })
      .catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [user?.logo_path]);

  const DEFAULT_TEXT =
    "in Sachen — wegen Verkehrsunfallschaden — wird hiermit Vollmacht erteilt zur Prozessführung " +
    "(insbesondere nach §§ 81 ff. ZPO), zur außergerichtlichen Vertretung sowie zur Entgegennahme " +
    "von Geldern, Wertsachen und Urkunden, insbesondere des Streitgegenstandes und der vom Gegner, " +
    "von der Justizkasse oder von sonstigen Stellen zu erstattenden Beträge.\n\n" +
    "Die Vollmacht erstreckt sich auf alle bei der Akte anfallenden Tätigkeiten in allen Rechtszügen " +
    "und schließt die Befugnis zur Erteilung von Untervollmachten ein.";

  // Load text: prefer mandant-specific, fall back to user template, then default
  useEffect(() => {
    if (mandant?.vollmacht_text) setText(mandant.vollmacht_text);
    else if (user?.vollmacht_template) setText(user.vollmacht_template);
    else setText(DEFAULT_TEXT);
    // eslint-disable-next-line
  }, [mandant?.id, user?.vollmacht_template]);

  // Restore signature from mandant if present
  useEffect(() => {
    if (mandant?.vollmacht_unterschrift && canvasRef.current) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(img, 0, 0, canvasRef.current.width, canvasRef.current.height);
        setHasSig(true);
      };
      img.src = mandant.vollmacht_unterschrift;
    }
  }, [mandant?.vollmacht_unterschrift]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t?.clientX ?? e.clientX) - r.left;
    const y = (t?.clientY ?? e.clientY) - r.top;
    return { x: x * (c.width / r.width), y: y * (c.height / r.height) };
  };
  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0A192F";
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke();
    setHasSig(true);
  };
  const stop = () => setDrawing(false);
  const clear = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSig(false);
  };
  const saveSig = () => {
    if (!hasSig) { toast.error("Bitte zuerst unterschreiben"); return; }
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  const saveText = async () => {
    if (!mandantId) { toast.error("Bitte zuerst Mandant speichern"); return; }
    setSavingText(true);
    try {
      await api.patch(`/mandanten/${mandantId}`, { vollmacht_text: text });
      toast.success("Vollmachts-Text gespeichert");
    } catch (err) { toast.error(errMsg(err, "Vollmachts-Text konnte nicht gespeichert werden")); } finally { setSavingText(false); }
  };

  const saveAsTemplate = async () => {
    setSavingTemplate(true);
    try {
      const r = await api.patch("/auth/me", { vollmacht_template: text });
      localStorage.setItem("kvr_user", JSON.stringify(r.data));
      toast.success("Als Standard-Vollmacht gespeichert — gilt für alle neuen Mandanten");
    } catch (err) { toast.error(errMsg(err, "Standard-Vollmacht konnte nicht gespeichert werden")); } finally { setSavingTemplate(false); }
  };

  const printSheet = () => {
    if (!sheetRef.current) return;
    // Make sure latest signature is on canvas
    if (canvasRef.current && hasSig) {
      const sigImg = canvasRef.current.toDataURL("image/png");
      const slot = sheetRef.current.querySelector("[data-print-sig]");
      if (slot) slot.style.backgroundImage = `url(${sigImg})`;
    }
    const w = window.open("", "_blank", "width=900,height=1200");
    w.document.write(`
      <!doctype html><html><head><meta charset="utf-8"/><title>Vollmacht</title>
      <style>
        @page { size: A4; margin: 0; }
        body { margin: 0; font-family: 'Times New Roman', serif; }
        .sheet { width: 210mm; min-height: 297mm; padding: 22mm 22mm 18mm 22mm; box-sizing: border-box; }
        h1 { font-family: serif; font-size: 28pt; letter-spacing: 0.3em; text-align: center; margin: 0 0 14mm 0; }
        .line { border-top: 1px solid #111; height: 1px; margin: 8mm 0; }
        .row { display: flex; justify-content: space-between; gap: 10mm; }
        .data { font-size: 11pt; line-height: 1.6; }
        .label { font-size: 9pt; color: #6B7280; text-transform: uppercase; letter-spacing: 0.08em; margin-top: 6mm; }
        .text { font-size: 11pt; line-height: 1.7; text-align: justify; white-space: pre-wrap; margin: 6mm 0 14mm 0; }
        .sig-box { border-bottom: 1px solid #111; height: 18mm; margin-top: 14mm; background-size: contain; background-repeat: no-repeat; background-position: left bottom; }
        .sig-cap { font-size: 9pt; color: #6B7280; margin-top: 1.5mm; }
        .footer { margin-top: 22mm; font-size: 9pt; color: #6B7280; text-align: center; }
      </style></head><body>
      ${sheetRef.current.outerHTML}
      <script>setTimeout(() => { window.print(); window.close(); }, 250);</script>
      </body></html>
    `);
    w.document.close();
  };

  // Anwaltsbriefkopf links/rechts
  const kanzleiName = user?.kanzlei_name || "—";
  const kanzleiAdr = [user?.kanzlei_adresse, [user?.kanzlei_plz, user?.kanzlei_ort].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
  const logoUrl = logoBlobUrl;
  const mandantName = mandant ? `${mandant.vorname || ""} ${mandant.nachname || ""}`.trim() : "—";
  const mandantAddr = mandant ? [mandant.strasse, [mandant.plz, mandant.ort].filter(Boolean).join(" ")].filter(Boolean).join(" · ") : "";
  const aktenzeichen = akte?.aktenzeichen || "—";

  return (
    <div className="space-y-4" data-testid="vollmacht-panel">
      {!mandantId && (
        <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-lg p-4 text-sm text-[#92400E]">
          Bitte zuerst den Mandanten im Reiter „Mandant" speichern, danach kann die Vollmacht ausgefüllt werden.
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={saveAsTemplate} disabled={savingTemplate} data-testid="vollmacht-save-template">
          Als Standard speichern
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={saveText} disabled={savingText || !mandantId} data-testid="vollmacht-save-text">
          Text speichern
        </Button>
        <Button type="button" size="sm" onClick={printSheet} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="vollmacht-print">
          Drucken / PDF
        </Button>
      </div>

      {/* A4-Sheet Vorschau — elegantes Vollmacht-Design (auf Handy/iPad automatisch skaliert) */}
      <div className="bg-[#E5E7EB] rounded-lg p-3 md:p-6 overflow-hidden">
        <FitA4>
        <div
          ref={sheetRef}
          className="sheet bg-white mx-auto shadow-xl"
          style={{
            width: "210mm",
            minHeight: "297mm",
            boxSizing: "border-box",
            fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
            color: "#0A192F",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative top bar (CI-Navy mit Gold-Akzent) */}
          <div style={{ height: "6mm", background: "#1A2238" }} />
          <div style={{ height: "1.5mm", background: "linear-gradient(90deg, #C8A14F 0%, #E5C97B 50%, #C8A14F 100%)" }} />

          <div style={{ padding: "16mm 22mm 18mm 22mm" }}>
            {/* Briefkopf-Mini — mit Logo + Kanzlei-Daten aus Profil */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: "9pt", color: "#4B5563", paddingBottom: "5mm", marginBottom: "12mm" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "5mm" }}>
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt={kanzleiName}
                    style={{ maxHeight: "18mm", maxWidth: "40mm", objectFit: "contain" }}
                  />
                )}
                <div>
                  <div style={{ fontWeight: 600, color: "#0A192F", fontSize: "10pt", letterSpacing: "0.05em" }}>{kanzleiName}</div>
                  <div style={{ marginTop: "0.5mm" }}>{kanzleiAdr}</div>
                  {user?.kanzlei_telefon && (
                    <div style={{ marginTop: "0.5mm", fontSize: "8.5pt" }}>Tel.: {user.kanzlei_telefon}</div>
                  )}
                  {user?.kanzlei_email && (
                    <div style={{ fontSize: "8.5pt" }}>{user.kanzlei_email}</div>
                  )}
                </div>
              </div>
              <div style={{ textAlign: "right", fontSize: "9pt" }}>
                <div style={{ display: "inline-block", padding: "2mm 4mm", border: "1px solid #1A2238", borderRadius: "1mm", background: "#FAFAFA" }}>
                  <div style={{ fontSize: "7.5pt", letterSpacing: "0.15em", color: "#6B7280", textTransform: "uppercase" }}>Aktenzeichen</div>
                  <div style={{ fontWeight: 700, color: "#0A192F", fontSize: "11pt", marginTop: "1mm" }}>{aktenzeichen}</div>
                </div>
              </div>
            </div>

            {/* Title — extra elegant mit Linien */}
            <div style={{ textAlign: "center", marginBottom: "16mm" }}>
              <div style={{ fontSize: "8.5pt", letterSpacing: "0.4em", color: "#C8A14F", fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 600, marginBottom: "3mm" }}>
                ANWALTSVOLLMACHT
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6mm" }}>
                <div style={{ flex: "0 0 35mm", height: "1px", background: "linear-gradient(90deg, transparent, #1A2238)" }} />
                <h1 style={{ fontSize: "32pt", letterSpacing: "0.18em", margin: 0, fontFamily: "serif", fontWeight: 600, color: "#1A2238" }}>
                  Vollmacht
                </h1>
                <div style={{ flex: "0 0 35mm", height: "1px", background: "linear-gradient(90deg, #1A2238, transparent)" }} />
              </div>
              <div style={{ fontSize: "9pt", color: "#6B7280", marginTop: "4mm", fontStyle: "italic" }}>
                {akte?.unfalldatum && `Verkehrsunfall vom ${new Date(akte.unfalldatum).toLocaleDateString("de-DE")}`}
              </div>
            </div>

            {/* Mandant-Block (2 Spalten mit dünnen Linien) */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12mm", marginBottom: "12mm" }}>
              <div>
                <div style={{ fontSize: "7.5pt", letterSpacing: "0.18em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: "1.5mm" }}>Vollmachtgeber</div>
                <div style={{ borderTop: "1px solid #1A2238", paddingTop: "2mm" }}>
                  <div style={{ fontSize: "13pt", fontWeight: 600, fontFamily: "serif", lineHeight: 1.3 }}>{mandantName || "—"}</div>
                  <div style={{ fontSize: "10pt", color: "#4B5563", marginTop: "1.5mm", lineHeight: 1.5 }}>{mandantAddr || "—"}</div>
                  {mandant?.geburtsdatum && (
                    <div style={{ fontSize: "9pt", color: "#6B7280", marginTop: "1mm" }}>
                      geboren am {new Date(mandant.geburtsdatum).toLocaleDateString("de-DE")}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "7.5pt", letterSpacing: "0.18em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: "1.5mm" }}>Bevollmächtigt</div>
                <div style={{ borderTop: "1px solid #1A2238", paddingTop: "2mm" }}>
                  <div style={{ fontSize: "13pt", fontWeight: 600, fontFamily: "serif", lineHeight: 1.3 }}>
                    {user?.full_name || user?.kanzlei_name || "—"}
                  </div>
                  {user?.anwalt_titel && (
                    <div style={{ fontSize: "10pt", color: "#4B5563", fontStyle: "italic", marginTop: "1.5mm" }}>{user.anwalt_titel}</div>
                  )}
                  <div style={{ fontSize: "9pt", color: "#6B7280", marginTop: "1mm" }}>{kanzleiName}</div>
                </div>
              </div>
            </div>

            {/* Editable Vollmacht-Text */}
            <div className="print:hidden" style={{ marginBottom: "8mm" }}>
              <Label className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">Vollmachts-Text (editierbar)</Label>
              <Textarea
                rows={9}
                value={text}
                onChange={(e) => setText(e.target.value)}
                style={{ fontFamily: "'Cormorant Garamond', 'Times New Roman', serif", fontSize: "12pt", lineHeight: 1.6 }}
                data-testid="vollmacht-text"
              />
            </div>

            {/* Static rendering of text for printing */}
            <div data-print-text style={{ display: "none" }}>{text}</div>

            {/* Decorative seal-area (Stempel-Look) */}
            <div style={{ position: "relative", marginTop: "16mm", marginBottom: "12mm" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16mm",
                alignItems: "flex-end",
              }}>
                <div>
                  <div style={{ borderBottom: "1px solid #1A2238", height: "16mm" }} />
                  <div style={{ fontSize: "8pt", letterSpacing: "0.12em", color: "#6B7280", textTransform: "uppercase", marginTop: "2mm" }}>
                    Ort &amp; Datum
                  </div>
                </div>
                <div>
                  <div
                    data-print-sig
                    style={{
                      borderBottom: "1px solid #1A2238",
                      height: "16mm",
                      backgroundImage: mandant?.vollmacht_unterschrift ? `url(${mandant.vollmacht_unterschrift})` : "none",
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "left bottom",
                    }}
                  />
                  <div style={{ fontSize: "8pt", letterSpacing: "0.12em", color: "#6B7280", textTransform: "uppercase", marginTop: "2mm" }}>
                    Unterschrift Vollmachtgeber
                  </div>
                  {mandant?.vollmacht_unterzeichnet_am && (
                    <div style={{ fontSize: "8pt", color: "#16A34A", marginTop: "1.5mm", fontStyle: "italic" }}>
                      ✓ digital signiert am {new Date(mandant.vollmacht_unterzeichnet_am).toLocaleDateString("de-DE")}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              marginTop: "20mm",
              paddingTop: "4mm",
              borderTop: "1px solid #E5E7EB",
              display: "flex",
              justifyContent: "space-between",
              fontSize: "8pt",
              color: "#9CA3AF",
              letterSpacing: "0.05em",
            }}>
              <span>{kanzleiName}</span>
              <span>Az. {aktenzeichen}</span>
              <span>Seite 1 / 1</span>
            </div>
          </div>

          {/* Decorative corner element (subtle) */}
          <div style={{
            position: "absolute",
            top: "20mm",
            right: "10mm",
            width: "0",
            height: "0",
            borderTop: "8mm solid #C8A14F",
            borderLeft: "8mm solid transparent",
            opacity: 0.18,
          }} />
        </div>
        </FitA4>
      </div>

      {/* Signature pad */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="overline text-[#D32F2F]">Unterschrift erfassen</p>
            <h4 className="font-serif text-lg">Mandant unterschreibt</h4>
            <p className="text-xs text-[#6B7280]">Touch / Maus / Trackpad</p>
          </div>
          {mandant?.vollmacht_unterzeichnet_am && (
            <span className="text-xs text-[#16A34A] flex items-center gap-1">
              <CheckCircle size={14} weight="fill" /> signiert am {new Date(mandant.vollmacht_unterzeichnet_am).toLocaleDateString("de-DE")}
            </span>
          )}
        </div>
        <div className="border-2 border-dashed border-[#E5E7EB] rounded-lg overflow-hidden bg-[#FAFAFA]">
          <canvas
            ref={canvasRef}
            width={800}
            height={200}
            className="w-full h-[160px] sm:h-[200px] bg-white touch-none"
            onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
            onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
            data-testid="vollmacht-canvas"
          />
        </div>
        <div className="flex justify-end gap-2 flex-wrap">
          <Button type="button" variant="outline" size="sm" onClick={clear} data-testid="vollmacht-clear">
            <X size={14} className="mr-1" /> Löschen
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!mandantId) { toast.error("Mandant zuerst speichern"); return; }
              if (!mandant?.email) { toast.error("Bitte E-Mail-Adresse beim Mandanten hinterlegen"); return; }
              if (!akte?.id) { toast.error("Akte nicht gefunden"); return; }
              try {
                const r = await api.post(`/akten/${akte.id}/portal/senden`, { base_url: window.location.origin });
                toast.success(`Portal-Zugang zum Unterschreiben an ${r.data.to} gesendet`);
              } catch (err) {
                const detail = err?.response?.data?.detail || "";
                if (detail.includes("SMTP nicht konfiguriert")) {
                  toast.error("Bitte zuerst SMTP in Einstellungen → E-Mail konfigurieren");
                } else {
                  toast.error(detail || "Fehler beim Versand");
                }
              }
            }}
            disabled={!mandantId}
            data-testid="vollmacht-send-email"
          >
            <PaperPlaneTilt size={14} className="mr-1" weight="duotone" /> Portal-Link senden
          </Button>
          <Button type="button" size="sm" onClick={saveSig} disabled={!mandantId || !hasSig} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="vollmacht-save">
            <CheckCircle size={14} className="mr-1" /> Unterschrift speichern
          </Button>
        </div>
      </div>
    </div>
  );
}

function GegnerPanel({ akte, docs, onSaved, onNext }) {
  const isOWi = akte?.art === "owi";
  const [g, setG] = useState({
    name: akte?.gegner?.name || "",
    kennzeichen: akte?.gegner?.kennzeichen || "",
    versicherung: akte?.gegner?.versicherung || "",
    versicherungsscheinnr: akte?.gegner?.versicherungsscheinnr || "",
    schadennr: akte?.gegner?.schadennr || "",
  });
  const [saving, setSaving] = useState(false);

  // Re-sync when akte updates (AI auto-fill)
  useEffect(() => {
    setG(prev => ({
      name: prev.name || akte?.gegner?.name || "",
      kennzeichen: prev.kennzeichen || akte?.gegner?.kennzeichen || "",
      versicherung: prev.versicherung || akte?.gegner?.versicherung || "",
      versicherungsscheinnr: prev.versicherungsscheinnr || akte?.gegner?.versicherungsscheinnr || "",
      schadennr: prev.schadennr || akte?.gegner?.schadennr || "",
    }));
    // eslint-disable-next-line
  }, [akte?.gegner?.name, akte?.gegner?.kennzeichen, akte?.gegner?.versicherung, akte?.gegner?.schadennr]);

  // KI-Werte aus Gutachten — fürs Highlighting
  const aiData = (() => (docs || []).find(d => d.analyzed && d.extracted_data)?.extracted_data || null)();
  const isAi = (key) => {
    if (!aiData) return false;
    if (key === "name") return !!aiData.verursacher_name && g.name === aiData.verursacher_name;
    if (key === "kennzeichen") return !!aiData.verursacher_kennzeichen && g.kennzeichen === aiData.verursacher_kennzeichen;
    if (key === "versicherung") return !!aiData.versicherung_gegner && g.versicherung === aiData.versicherung_gegner;
    if (key === "schadennr") return !!aiData.schadennummer && g.schadennr === aiData.schadennummer;
    return false;
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/akten/${akte.id}`, { gegner: g });
      toast.success("Gespeichert");
      onSaved?.();
      onNext?.();
    } catch (err) { toast.error(errMsg(err, "Speichern fehlgeschlagen")); } finally { setSaving(false); }
  };

  return (
    <PanelShell title={isOWi ? "Behörde / Bußgeldstelle" : "Gegner / Versicherung"} hint={isOWi ? "Wer hat den Bescheid erlassen?" : "Unfallgegner und seine Versicherung."} onSubmit={save} saving={saving} onSkip={onNext}>
      <div>
        <Label className="flex items-center">{isOWi ? "Behörde" : "Name des Gegners"}{isAi("name") && <AiBadge />}</Label>
        <Input value={g.name} onChange={(e) => setG({ ...g, name: e.target.value })} placeholder={isOWi ? "z.B. Stadt Köln, Bußgeldstelle" : "z.B. Max Schmitt"} data-testid="capture-gegner-name" className={aiFilledClass(isAi("name"))} />
      </div>
      {!isOWi && (
        <>
          <div>
            <Label className="flex items-center">Kennzeichen{isAi("kennzeichen") && <AiBadge />}</Label>
            <Input value={g.kennzeichen} onChange={(e) => setG({ ...g, kennzeichen: e.target.value })} placeholder="K-AB 1234" className={aiFilledClass(isAi("kennzeichen"))} />
          </div>
          <div>
            <Label className="flex items-center">Versicherung{isAi("versicherung") && <AiBadge />}</Label>
            <Input value={g.versicherung} onChange={(e) => setG({ ...g, versicherung: e.target.value })} placeholder="z.B. HUK-Coburg" data-testid="capture-gegner-versicherung" className={aiFilledClass(isAi("versicherung"))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>VS-Schein-Nr.</Label>
              <Input value={g.versicherungsscheinnr} onChange={(e) => setG({ ...g, versicherungsscheinnr: e.target.value })} />
            </div>
            <div>
              <Label className="flex items-center">Schaden-Nr.{isAi("schadennr") && <AiBadge />}</Label>
              <Input value={g.schadennr} onChange={(e) => setG({ ...g, schadennr: e.target.value })} className={aiFilledClass(isAi("schadennr"))} />
            </div>
          </div>
        </>
      )}
      {isOWi && (
        <div>
          <Label>Aktenzeichen der Behörde</Label>
          <Input value={g.schadennr} onChange={(e) => setG({ ...g, schadennr: e.target.value })} placeholder="z.B. 28.41-12345/26" />
        </div>
      )}
    </PanelShell>
  );
}

function FahrzeugPanel({ akte, docs, onSaved, onNext }) {
  const isOWi = akte?.art === "owi";
  const [f, setF] = useState({
    marke: akte?.fahrzeug?.marke || "",
    modell: akte?.fahrzeug?.modell || "",
    kennzeichen: akte?.fahrzeug?.kennzeichen || "",
    erstzulassung: akte?.fahrzeug?.erstzulassung || "",
    laufleistung: akte?.fahrzeug?.laufleistung || "",
  });
  const [unfalldatum, setUnfalldatum] = useState(akte?.unfalldatum?.slice(0, 10) || "");
  const [unfallort, setUnfallort] = useState(akte?.unfallort || "");
  const [unfallhergang, setUnfallhergang] = useState(akte?.unfallhergang || "");
  const [saving, setSaving] = useState(false);

  // Re-sync from akte (AI auto-fill)
  useEffect(() => {
    setF(prev => ({
      marke: prev.marke || akte?.fahrzeug?.marke || "",
      modell: prev.modell || akte?.fahrzeug?.modell || "",
      kennzeichen: prev.kennzeichen || akte?.fahrzeug?.kennzeichen || "",
      erstzulassung: prev.erstzulassung || akte?.fahrzeug?.erstzulassung || "",
      laufleistung: prev.laufleistung || akte?.fahrzeug?.laufleistung || "",
    }));
    if (!unfalldatum && akte?.unfalldatum) setUnfalldatum(akte.unfalldatum.slice(0, 10));
    if (!unfallort && akte?.unfallort) setUnfallort(akte.unfallort);
    if (!unfallhergang && akte?.unfallhergang) setUnfallhergang(akte.unfallhergang);
    // eslint-disable-next-line
  }, [akte?.fahrzeug?.kennzeichen, akte?.fahrzeug?.marke, akte?.unfalldatum, akte?.unfallort]);

  // KI-Werte fürs Highlighting
  const aiData = (() => (docs || []).find(d => d.analyzed && d.extracted_data)?.extracted_data || null)();
  const isAi = (key) => {
    if (!aiData) return false;
    if (key === "marke") return !!aiData.fahrzeug_marke && f.marke === aiData.fahrzeug_marke;
    if (key === "modell") return !!aiData.fahrzeug_modell && f.modell === aiData.fahrzeug_modell;
    if (key === "kennzeichen") return !!aiData.kennzeichen && f.kennzeichen === aiData.kennzeichen;
    if (key === "erstzulassung") return !!aiData.erstzulassung && f.erstzulassung === aiData.erstzulassung;
    if (key === "unfalldatum") return !!aiData.unfalldatum && unfalldatum === aiData.unfalldatum;
    if (key === "unfallort") return !!aiData.unfallort && unfallort === aiData.unfallort;
    if (key === "unfallhergang") return !!aiData.schadenbeschreibung && unfallhergang === aiData.schadenbeschreibung;
    return false;
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/akten/${akte.id}`, {
        fahrzeug: f,
        unfalldatum: unfalldatum || null,
        unfallort: unfallort || null,
        unfallhergang: unfallhergang || null,
      });
      toast.success("Gespeichert");
      onSaved?.();
      onNext?.();
    } catch (err) { toast.error(errMsg(err, "Speichern fehlgeschlagen")); } finally { setSaving(false); }
  };

  return (
    <PanelShell
      title={isOWi ? "Vorgang / Tatort" : "Fahrzeug & Unfallhergang"}
      hint={isOWi ? "Wann und wo wurde der Verstoß aufgenommen?" : "Mandantenfahrzeug und Eckdaten zum Unfall."}
      onSubmit={save}
      saving={saving}
      onSkip={onNext}
    >
      {!isOWi && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="flex items-center">Marke{isAi("marke") && <AiBadge />}</Label>
            <Input value={f.marke} onChange={(e) => setF({ ...f, marke: e.target.value })} placeholder="z.B. BMW" className={aiFilledClass(isAi("marke"))} />
          </div>
          <div>
            <Label className="flex items-center">Modell{isAi("modell") && <AiBadge />}</Label>
            <Input value={f.modell} onChange={(e) => setF({ ...f, modell: e.target.value })} placeholder="z.B. 320d" className={aiFilledClass(isAi("modell"))} />
          </div>
          <div>
            <Label className="flex items-center">Kennzeichen{isAi("kennzeichen") && <AiBadge />}</Label>
            <Input value={f.kennzeichen} onChange={(e) => setF({ ...f, kennzeichen: e.target.value })} placeholder="K-XY 5678" data-testid="capture-fahrzeug-kennzeichen" className={aiFilledClass(isAi("kennzeichen"))} />
          </div>
          <div>
            <Label className="flex items-center">Erstzulassung{isAi("erstzulassung") && <AiBadge />}</Label>
            <Input value={f.erstzulassung} onChange={(e) => setF({ ...f, erstzulassung: e.target.value })} placeholder="z.B. 03/2019" className={aiFilledClass(isAi("erstzulassung"))} />
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="flex items-center">{isOWi ? "Tatdatum" : "Unfalldatum"}{isAi("unfalldatum") && <AiBadge />}</Label>
          <Input type="date" value={unfalldatum} onChange={(e) => setUnfalldatum(e.target.value)} data-testid="capture-unfalldatum" className={aiFilledClass(isAi("unfalldatum"))} />
        </div>
        <div>
          <Label className="flex items-center">{isOWi ? "Tatort" : "Unfallort"}{isAi("unfallort") && <AiBadge />}</Label>
          <Input value={unfallort} onChange={(e) => setUnfallort(e.target.value)} placeholder="z.B. Köln, Hohenzollernring 12" className={aiFilledClass(isAi("unfallort"))} />
        </div>
      </div>
      {!isOWi && (
        <div>
          <Label>Unfallhergang (Stichpunkte)</Label>
          <Textarea rows={3} value={unfallhergang} onChange={(e) => setUnfallhergang(e.target.value)} placeholder="Kurze Beschreibung des Unfallhergangs" />
        </div>
      )}
    </PanelShell>
  );
}

function CtaPanel({ title, text, buttonLabel, onClick, href }) {
  const Btn = href ? (
    <a href={href}>
      <Button className="bg-[#1A2238] hover:bg-[#2C3859]">
        {buttonLabel} <ArrowRight size={14} className="ml-2" />
      </Button>
    </a>
  ) : (
    <Button className="bg-[#1A2238] hover:bg-[#2C3859]" onClick={onClick}>
      {buttonLabel} <ArrowRight size={14} className="ml-2" />
    </Button>
  );
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 space-y-4">
      <h2 className="font-serif text-2xl">{title}</h2>
      <p className="text-sm text-[#4B5563] max-w-xl leading-relaxed">{text}</p>
      <div>{Btn}</div>
    </div>
  );
}

function RvgPanel({ akte, schriftsaetze, onSaved, onNext }) {
  const eur = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
  const bez = akte?.bezifferung || {};
  const streitwert = bez.streitwert || 0;
  const rvg = bez.rvg_gebuehr || 0;
  const [generating, setGenerating] = useState(false);

  // Existing RVG-Honorar als Schriftsatz finden (auto-generiert nach Gutachten-Scan)
  const honorar = (schriftsaetze || []).find(s => s.typ === "rvg_honorar");
  const generateHonorar = async () => {
    if (honorar) {
      toast.info("Honorarrechnung wurde bereits erstellt — siehe unten");
      return;
    }
    setGenerating(true);
    try {
      await api.post("/schriftsaetze/generate", {
        akte_id: akte.id,
        typ: "rvg_honorar",
        zusatz: "",
      });
      toast.success("Honorarrechnung erstellt");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e, "Honorarrechnung konnte nicht erstellt werden"));
    } finally { setGenerating(false); }
  };

  if (!streitwert) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 space-y-3" data-testid="rvg-empty">
        <h2 className="font-serif text-2xl">Honorar nach RVG</h2>
        <p className="text-sm text-[#6B7280] max-w-xl">
          Sobald die Bezifferung erfolgt ist (Streitwert &gt; 0 €), wird die Anwaltsgebühr nach Rechtsanwaltsvergütungsgesetz hier automatisch ausgewiesen.
        </p>
      </div>
    );
  }

  const post = bez.unkostenpauschale || 25;
  const ust = (rvg + post) * 0.19;
  const total = rvg + post + ust;

  return (
    <div className="space-y-4" data-testid="rvg-panel">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-[#1A2238] text-white flex items-center justify-center shrink-0">
            <Calculator size={20} weight="duotone" />
          </div>
          <div>
            <p className="overline text-[#D32F2F]">RVG · § 13</p>
            <h2 className="font-serif text-2xl">Honorar nach Rechtsanwaltsvergütungsgesetz</h2>
            <p className="text-sm text-[#6B7280] mt-1">
              Berechnet aus dem Streitwert nach Anlage 2 zum RVG (1,3-fache Geschäftsgebühr).
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <RvgRow label="Streitwert (Gegenstandswert)" value={eur(streitwert)} muted />
          <RvgRow label="1,3 Geschäftsgebühr (Nr. 2300 VV RVG)" value={eur(rvg)} bold />
          <RvgRow label="Auslagenpauschale (Nr. 7002 VV RVG)" value={eur(post)} muted />
          <RvgRow label="USt. 19 % (Nr. 7008 VV RVG)" value={eur(ust)} muted />
          <div className="col-span-2 mt-2 pt-3 border-t border-[#E5E7EB] flex items-center justify-between">
            <span className="font-serif text-lg">Gesamt</span>
            <span className="font-serif text-2xl text-[#1A2238]">{eur(total)}</span>
          </div>
        </div>

        <p className="text-xs text-[#9CA3AF] pt-2">
          Hinweis: Die Erstattung der Anwaltskosten richtet sich nach § 249 BGB als Teil des Schadens (vgl. BGH, Urt. v. 27.07.2010 – VI ZR 261/09).
        </p>
      </div>

      <div className="flex items-center justify-end gap-2">
        {onNext && (
          <Button variant="outline" onClick={onNext} data-testid="rvg-next">
            Weiter zum Versand <ArrowRight size={14} className="ml-2" />
          </Button>
        )}
        <Button
          onClick={generateHonorar}
          disabled={generating || !!honorar}
          className="bg-[#1A2238] hover:bg-[#2C3859]"
          data-testid="rvg-generate-btn"
        >
          {generating ? <Spinner size={14} className="mr-2 animate-spin" /> : <FileText size={14} className="mr-2" weight="duotone" />}
          {honorar ? "Honorarrechnung erstellt" : "Honorarrechnung erstellen"}
        </Button>
      </div>

      {/* Inline A4-Vorschau, falls Honorarrechnung schon existiert */}
      {honorar && <SchriftsatzInlinePreview schriftsatz={honorar} akte={akte} />}
    </div>
  );
}

function RvgRow({ label, value, bold, muted, aiFilled }) {
  const aiCls = aiFilled ? "bg-[#EFF6FF] border border-[#BFDBFE] rounded px-2 py-1.5" : "";
  return (
    <div className={`flex flex-col ${aiCls}`}>
      <span className={`text-[10px] uppercase tracking-wide ${muted ? "text-[#9CA3AF]" : "text-[#6B7280]"}`}>
        {label}
      </span>
      <span className={`tabular-nums ${aiFilled ? "text-[#1D4ED8] font-medium" : ""} ${bold ? "text-xl font-serif text-[#0A192F]" : "text-base text-[#4B5563]"}`}>{value}</span>
    </div>
  );
}


// ----- Inline-Panels für Bezifferung / Schriftsätze / Versand -----

const SCHADENPOS_PRESETS = [
  "Abschleppkosten", "Mietwagenkosten", "An-/Abmeldekosten", "Standgeld", "Schmerzensgeld", "Attestkosten",
  "Haushaltsführungsschaden", "Verdienstausfall", "Fahrtkosten", "Entsorgungskosten", "Kindersitz / Zubehör", "Sonstiges",
];

function SchadenartSwitch({ bez, onChange, disabled }) {
  const current = bez?.schadenart_quelle === "manuell" && bez?.schadenart ? bez.schadenart : "auto";
  const opts = [["auto", "Automatisch"], ["reparaturschaden", "Reparaturschaden"], ["130_prozent", "130 %-Fall"], ["totalschaden", "Totalschaden"]];
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] uppercase tracking-wide text-[#9CA3AF]">Abrechnung als</p>
      <div className="inline-flex flex-wrap gap-0.5 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-0.5" role="tablist" data-testid="schadenart-switch">
        {opts.map(([v, l]) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={current === v}
            disabled={disabled}
            onClick={() => current !== v && onChange(v)}
            data-testid={`schadenart-switch-${v}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${current === v ? "bg-[#1A2238] text-white shadow-sm" : "text-[#4B5563] hover:bg-white"}`}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

function SchadenartBadge({ bez }) {
  const meta = SCHADENART_META[bez?.schadenart];
  if (!meta) return null;
  const quelle = SCHADENART_QUELLE[bez.schadenart_quelle] || "";
  return (
    <div className="space-y-1.5" data-testid="schadenart-badge">
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${meta.cls}`} data-testid={`schadenart-${bez.schadenart}`}>
        <Car size={12} weight="duotone" /> {meta.label}
        {quelle && <span className="opacity-70 font-normal">· {quelle}</span>}
      </span>
      {bez.schadenart_hinweis && (
        <p className="text-xs text-[#6B7280] leading-relaxed" data-testid="schadenart-hinweis">{bez.schadenart_hinweis}</p>
      )}
    </div>
  );
}

function BezifferungInlinePanel({ akte, docs, schriftsaetze, onSaved, onNext }) {
  const { user } = useAuth();
  const { track } = useActivity();
  const eur = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
  const bez = akte?.bezifferung || {};
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [genPending, setGenPending] = useState(false);
  const [form, setForm] = useState({
    reparaturkosten: bez.reparaturkosten || 0,
    wertminderung: bez.wertminderung || 0,
    wiederbeschaffungswert: bez.wiederbeschaffungswert || 0,
    restwert: bez.restwert || 0,
    nutzungsausfall_tage: bez.nutzungsausfall_tage || 0,
    fahrzeugklasse: bez.fahrzeugklasse || "D",
    sachverstaendigenkosten: bez.sachverstaendigenkosten || 0,
    schadenart: bez.schadenart_quelle === "manuell" && bez.schadenart ? bez.schadenart : "auto",
    weitere_positionen: bez.weitere_positionen || [],
  });

  // Sync form when akte updates (e.g. after KI-Auto-Apply)
  useEffect(() => {
    setForm(f => ({
      ...f,
      reparaturkosten: bez.reparaturkosten || f.reparaturkosten,
      wertminderung: bez.wertminderung || f.wertminderung,
      wiederbeschaffungswert: bez.wiederbeschaffungswert || f.wiederbeschaffungswert,
      restwert: bez.restwert || f.restwert,
      nutzungsausfall_tage: bez.nutzungsausfall_tage || f.nutzungsausfall_tage,
      fahrzeugklasse: bez.fahrzeugklasse || f.fahrzeugklasse,
      sachverstaendigenkosten: bez.sachverstaendigenkosten || f.sachverstaendigenkosten,
      schadenart: bez.schadenart_quelle === "manuell" && bez.schadenart ? bez.schadenart : f.schadenart,
      weitere_positionen: editing ? f.weitere_positionen : (bez.weitere_positionen || []),
    }));
    // eslint-disable-next-line
  }, [bez.reparaturkosten, bez.wertminderung, bez.wiederbeschaffungswert, bez.restwert, bez.nutzungsausfall_tage, bez.sachverstaendigenkosten, bez.fahrzeugklasse, bez.schadenart, bez.schadenart_quelle, bez.weitere_positionen_summe]);

  const payload = (override = {}) => ({
    ...form,
    ...override,
    schadenart: (override.schadenart ?? form.schadenart) === "auto" ? null : (override.schadenart ?? form.schadenart),
    weitere_positionen: (form.weitere_positionen || []).filter(p => (p.bezeichnung || "").trim()).map(p => ({ ...p, betrag: Number(p.betrag) || 0 })),
  });

  const recompute = async () => {
    setSaving(true);
    try {
      await api.post(`/akten/${akte.id}/bezifferung`, payload());
      toast.success("Bezifferung aktualisiert");
      setEditing(false);
      onSaved?.();
    } catch (err) { toast.error(errMsg(err, "Bezifferung konnte nicht gespeichert werden")); } finally { setSaving(false); }
  };

  const switchSchadenart = async (v) => {
    setSaving(true);
    try {
      await api.post(`/akten/${akte.id}/bezifferung`, payload({ schadenart: v }));
      setForm(f => ({ ...f, schadenart: v }));
      toast.success(v === "auto" ? "Schadenart wird automatisch ermittelt" : `Abrechnung als ${SCHADENART_META[v]?.label || v}`);
      onSaved?.();
    } catch (err) { toast.error(errMsg(err, "Schadenart konnte nicht geändert werden")); } finally { setSaving(false); }
  };

  const setPos = (idx, patch) => setForm(f => ({ ...f, weitere_positionen: f.weitere_positionen.map((p, i) => i === idx ? { ...p, ...patch } : p) }));
  const addPos = (bezeichnung = "") => setForm(f => ({ ...f, weitere_positionen: [...(f.weitere_positionen || []), { bezeichnung, betrag: 0, notiz: "" }] }));
  const removePos = (idx) => setForm(f => ({ ...f, weitere_positionen: f.weitere_positionen.filter((_, i) => i !== idx) }));

  const reAnalyseFromDocs = async () => {
    const analyzedDocs = (docs || []).filter(d => d.analyzed);
    if (analyzedDocs.length === 0) {
      toast.error("Keine analysierten Dokumente — bitte zuerst Gutachten hochladen");
      return;
    }
    setRecomputing(true);
    try {
      // Re-trigger analyze auf erstes analysiertes Doc → läuft im Hintergrund
      await api.post(`/dokumente/${analyzedDocs[0].id}/analyze`);
      toast.info("Bezifferung wird neu aus dem Gutachten berechnet …");
      setTimeout(() => onSaved?.(), 4000);
    } catch (err) { toast.error(errMsg(err, "Neu-Berechnung aus dem Gutachten fehlgeschlagen")); }
    finally { setRecomputing(false); }
  };

  const hasBezifferung = (bez.streitwert || 0) > 0;
  const isTotal = bez.schadenart === "totalschaden";
  const is130 = bez.schadenart === "130_prozent";

  return (
    <div className="space-y-4" data-testid="bezifferung-inline-panel">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="overline text-[#D32F2F] mb-1">Schadenbezifferung</p>
            <h2 className="font-serif text-2xl">Schaden in Zahlen</h2>
            <p className="text-sm text-[#6B7280] mt-1">
              {hasBezifferung
                ? "Aus dem Gutachten extrahiert — du kannst Werte manuell anpassen."
                : "Lade ein Gutachten hoch — die Werte werden automatisch eingetragen."}
            </p>
          </div>
          <div className="flex gap-2">
            {hasBezifferung && !editing && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="bez-edit-btn">
                Werte anpassen
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={reAnalyseFromDocs} disabled={recomputing} data-testid="bez-reanalyze-btn">
              {recomputing && <Spinner size={12} className="mr-1.5 animate-spin" />}
              Aus Gutachten neu
            </Button>
          </div>
        </div>

        {hasBezifferung && (
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 flex-wrap">
            <SchadenartBadge bez={bez} />
            {!editing && <SchadenartSwitch bez={bez} onChange={switchSchadenart} disabled={saving} />}
          </div>
        )}

        {hasBezifferung && !editing ? (
          <div className="grid grid-cols-2 gap-3">
            {isTotal ? (
              <>
                <RvgRow label="Wiederbeschaffungswert" value={eur(bez.wiederbeschaffungswert)} muted aiFilled={(bez.wiederbeschaffungswert || 0) > 0} />
                <RvgRow label="Restwert (abzgl.)" value={`− ${eur(bez.restwert)}`} muted aiFilled={(bez.restwert || 0) > 0} />
                <RvgRow label="Wiederbeschaffungsaufwand (WBW − RW)" value={eur(bez.wiederbeschaffungsaufwand ?? bez.schaden_fahrzeug)} muted aiFilled />
                <RvgRow label="Reparaturkosten (nicht angesetzt)" value={eur(bez.reparaturkosten)} muted />
                <RvgRow label="Wertminderung (entfällt bei Totalschaden)" value={eur(0)} muted />
              </>
            ) : (
              <>
                <RvgRow label={is130 ? "Reparaturkosten (130 %-Regel)" : "Reparaturkosten"} value={eur(bez.reparaturkosten)} muted aiFilled={(bez.reparaturkosten || 0) > 0} />
                <RvgRow label="Wertminderung" value={eur(bez.wertminderung)} muted aiFilled={(bez.wertminderung || 0) > 0} />
                <RvgRow label="Wiederbeschaffungswert" value={eur(bez.wiederbeschaffungswert)} muted aiFilled={(bez.wiederbeschaffungswert || 0) > 0} />
              </>
            )}
            <RvgRow label={`Nutzungsausfall (${bez.nutzungsausfall_tage || 0} Tage × ${eur(bez.nutzungsausfall_satz)})`} value={eur(bez.nutzungsausfall_gesamt)} muted aiFilled={(bez.nutzungsausfall_tage || 0) > 0} />
            <RvgRow label="Sachverständigenkosten" value={eur(bez.sachverstaendigenkosten)} muted aiFilled={(bez.sachverstaendigenkosten || 0) > 0} />
            <RvgRow label="Auslagenpauschale" value={eur(bez.unkostenpauschale || 25)} muted />
            {(bez.weitere_positionen || []).map((p, i) => (
              <RvgRow key={p.id || i} label={p.bezeichnung + (p.notiz ? ` — ${p.notiz}` : "")} value={eur(p.betrag)} muted />
            ))}
            <div className="col-span-2 flex justify-start">
              <button
                type="button"
                onClick={() => { addPos(); setEditing(true); }}
                className="text-xs text-[#1A2238] underline-offset-2 hover:underline inline-flex items-center gap-1"
                data-testid="bez-add-position-btn"
              >
                <Plus size={12} /> Weitere Schadenposition hinzufügen
              </button>
            </div>
            <div className="col-span-2 mt-3 pt-3 border-t border-[#E5E7EB] flex items-center justify-between">
              <span className="font-serif text-lg">Streitwert (Gegenstandswert)</span>
              <span className="font-serif text-3xl text-[#1A2238]">{eur(bez.streitwert)}</span>
            </div>
            <div className="col-span-2 flex items-center justify-between text-sm">
              <span className="text-[#6B7280]">RVG-Gebühr (1,3-Geschäftsgebühr inkl. USt.)</span>
              <span className="font-mono font-semibold text-[#D32F2F]">{eur(bez.rvg_gebuehr)}</span>
            </div>
          </div>
        ) : editing || !hasBezifferung ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["reparaturkosten", "Reparaturkosten (€)"],
                ["wertminderung", "Wertminderung (€)"],
                ["wiederbeschaffungswert", "Wiederbeschaffungswert (€)"],
                ["restwert", "Restwert (€)"],
                ["nutzungsausfall_tage", "Nutzungsausfall (Tage)"],
                ["sachverstaendigenkosten", "Sachverständigenkosten (€)"],
              ].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs text-[#6B7280]">{l}</Label>
                  <Input
                    type="number"
                    value={form[k]}
                    onChange={e => setForm(f => ({ ...f, [k]: parseFloat(e.target.value) || 0 }))}
                    data-testid={`bez-form-${k}`}
                  />
                </div>
              ))}
              <div>
                <Label className="text-xs text-[#6B7280]">Schadenart</Label>
                <Select value={form.schadenart} onValueChange={v => setForm(f => ({ ...f, schadenart: v }))}>
                  <SelectTrigger data-testid="bez-form-schadenart"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automatisch (Gutachten / 130 %-Regel)</SelectItem>
                    <SelectItem value="reparaturschaden">Reparaturschaden</SelectItem>
                    <SelectItem value="130_prozent">130 %-Fall</SelectItem>
                    <SelectItem value="totalschaden">Wirtschaftlicher Totalschaden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Fahrzeugklasse (Nutzungsausfall)</Label>
                <Select value={form.fahrzeugklasse} onValueChange={v => setForm(f => ({ ...f, fahrzeugklasse: v }))}>
                  <SelectTrigger data-testid="bez-form-fahrzeugklasse"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["A", "B", "C", "D", "E", "F", "G", "H", "J", "K", "L"].map(g => (
                      <SelectItem key={g} value={g}>Gruppe {g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-2 border-t border-[#E5E7EB] space-y-2" data-testid="bez-positionen-editor">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-wide text-[#9CA3AF]">Weitere Schadenpositionen</p>
                <Button type="button" variant="outline" size="sm" onClick={() => addPos()} data-testid="bez-pos-add">
                  <Plus size={12} className="mr-1" /> Position
                </Button>
              </div>
              <datalist id="schadenpos-presets">
                {SCHADENPOS_PRESETS.map(p => <option key={p} value={p} />)}
              </datalist>
              {(form.weitere_positionen || []).length === 0 && (
                <p className="text-xs text-[#9CA3AF]">z. B. Abschleppkosten, Mietwagen, An-/Abmeldekosten, Schmerzensgeld …</p>
              )}
              {(form.weitere_positionen || []).map((p, i) => (
                <div key={p.id || i} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center" data-testid={`bez-pos-row-${i}`}>
                  <Input
                    list="schadenpos-presets"
                    placeholder="Bezeichnung (z. B. Abschleppkosten)"
                    value={p.bezeichnung || ""}
                    onChange={e => setPos(i, { bezeichnung: e.target.value })}
                    data-testid={`bez-pos-bezeichnung-${i}`}
                  />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="€"
                    value={p.betrag ?? ""}
                    onChange={e => setPos(i, { betrag: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                    data-testid={`bez-pos-betrag-${i}`}
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removePos(i)} title="Entfernen" data-testid={`bez-pos-remove-${i}`}>
                    <Trash size={14} className="text-[#D32F2F]" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              {editing && (
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Abbrechen</Button>
              )}
              <Button onClick={recompute} disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="bez-form-save">
                {saving && <Spinner size={12} className="mr-1.5 animate-spin" />}
                Bezifferung berechnen
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {hasBezifferung && onNext && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={onNext} data-testid="bez-next">
            Weiter zum Anspruchsschreiben <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
      )}

      {/* Inline A4-Vorschau des Bezifferungsschreibens */}
      {hasBezifferung && (() => {
        const brief = (schriftsaetze || []).find(s => s.typ === "bezifferungsschreiben");
        if (brief) {
          return (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="overline text-[#D32F2F]">Bezifferungsschreiben</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm("Bezifferungsschreiben neu generieren? Bisheriger Entwurf wird überschrieben.")) return;
                    const stop = track("Bezifferungsschreiben wird neu erstellt");
                    setGenPending(true);
                    try {
                      await api.delete(`/schriftsaetze/${brief.id}`);
                      await api.post("/schriftsaetze/generate", { akte_id: akte.id, typ: "bezifferungsschreiben", zusatz: "" });
                      toast.success("Bezifferungsschreiben aktualisiert");
                      onSaved?.();
                    } catch (err) { toast.error(errMsg(err, "Bezifferungsschreiben konnte nicht erstellt werden")); }
                    finally { stop(); setGenPending(false); }
                  }}
                  disabled={genPending}
                  data-testid="bez-brief-regenerate"
                >
                  {genPending ? <Spinner size={12} className="mr-1.5 animate-spin" /> : <Sparkle size={12} className="mr-1.5" weight="duotone" />}
                  Neu generieren
                </Button>
              </div>
              <SchriftsatzInlinePreview schriftsatz={brief} akte={akte} user={user} />
            </div>
          );
        }
        return (
          <div className="bg-white border border-dashed border-[#E5E7EB] rounded-lg p-6 flex flex-wrap items-center justify-between gap-3" data-testid="bez-brief-pending">
            <div className="min-w-0">
              <p className="overline text-[#D32F2F] mb-0.5">Bezifferungsschreiben</p>
              <p className="text-sm text-[#0A192F] font-medium">
                {genPending ? "Wird erstellt …" : "Wird im Hintergrund vorbereitet — taucht hier auf, sobald fertig"}
              </p>
              <p className="text-xs text-[#6B7280] mt-1">
                Brief an die gegnerische Versicherung mit allen Schadenpositionen + Zahlungsfrist (14 Tage).
              </p>
            </div>
            <Button
              onClick={async () => {
                const stop = track("Bezifferungsschreiben wird erstellt");
                setGenPending(true);
                try {
                  await api.post("/schriftsaetze/generate", { akte_id: akte.id, typ: "bezifferungsschreiben", zusatz: "" });
                  toast.success("Bezifferungsschreiben erstellt");
                  onSaved?.();
                } catch (err) {
                  if (err?.response?.status >= 502 || err?.code === "ECONNABORTED" || !err?.response) {
                    toast.info("Wird im Hintergrund weitergearbeitet — gleich erscheint es hier");
                    setTimeout(() => onSaved?.(), 30000);
                  } else {
                    toast.error(errMsg(err, "Bezifferungsschreiben konnte nicht erstellt werden"));
                  }
                } finally { stop(); setGenPending(false); }
              }}
              disabled={genPending}
              size="sm"
              className="bg-[#1A2238] hover:bg-[#2C3859]"
              data-testid="bez-brief-generate"
            >
              {genPending ? <Spinner size={12} className="mr-1.5 animate-spin" /> : <Sparkle size={12} className="mr-1.5" weight="duotone" />}
              Jetzt generieren
            </Button>
          </div>
        );
      })()}
    </div>
  );
}

function SchriftsatzInlinePanel({ akte, schriftsaetze, typ, title, onSaved, onNext }) {
  const { user } = useAuth();
  const existing = (schriftsaetze || []).find(s => s.typ === typ);
  const [generating, setGenerating] = useState(false);
  const [zusatz, setZusatz] = useState("");
  const [editing, setEditing] = useState(false);

  const generate = async () => {
    setGenerating(true);
    const tId = toast.loading(`${title} wird im Hintergrund erstellt — kann ~30–60 s dauern …`);
    try {
      await api.post("/schriftsaetze/generate", { akte_id: akte.id, typ, zusatz });
      toast.dismiss(tId);
      toast.success(`${title} erstellt`);
      setZusatz("");
      onSaved?.();
    } catch (err) {
      toast.dismiss(tId);
      // 502/504 vom Ingress = Backend läuft trotzdem im Hintergrund weiter
      if (err?.response?.status >= 502 || err?.code === "ECONNABORTED" || !err?.response) {
        toast.info(`${title} wird im Hintergrund erstellt — bitte gleich aktualisieren`);
        setTimeout(() => onSaved?.(), 30000);
      } else {
        toast.error(errMsg(err, `${title} konnte nicht erstellt werden`), { duration: 8000 });
      }
    } finally { setGenerating(false); }
  };

  const regenerate = async () => {
    if (!existing) return;
    if (!window.confirm(`${title} neu generieren? Der bisherige Entwurf wird überschrieben.`)) return;
    setGenerating(true);
    try {
      await api.delete(`/schriftsaetze/${existing.id}`);
      await generate();
    } catch (err) { toast.error(errMsg(err, "Alter Entwurf konnte nicht gelöscht werden")); setGenerating(false); }
  };

  return (
    <div className="space-y-4" data-testid={`schriftsatz-inline-${typ}`}>
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="overline text-[#D32F2F] mb-1">Schriftsatz</p>
            <h2 className="font-serif text-2xl">{title}</h2>
            <p className="text-sm text-[#6B7280] mt-1">
              {existing
                ? `Erstellt am ${new Date(existing.created_at).toLocaleString("de-DE")} — Vorschau unten, du kannst direkt drucken.`
                : "Wird im Hintergrund vorbereitet, sobald die Bezifferung steht. Du kannst es auch manuell anstoßen."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {existing ? (
              <Button variant="outline" size="sm" onClick={regenerate} disabled={generating} data-testid={`schriftsatz-regenerate-${typ}`}>
                {generating ? <Spinner size={12} className="mr-1.5 animate-spin" /> : <Sparkle size={12} className="mr-1.5" weight="duotone" />}
                Neu generieren
              </Button>
            ) : (
              <Button onClick={generate} disabled={generating} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid={`schriftsatz-generate-${typ}`}>
                {generating ? <Spinner size={14} className="mr-2 animate-spin" /> : <Sparkle size={14} className="mr-2" weight="duotone" />}
                Jetzt generieren
              </Button>
            )}
          </div>
        </div>

        {!existing && (
          <div>
            <Label className="text-xs text-[#6B7280]">Zusätzliche Hinweise (optional)</Label>
            <Input
              value={zusatz}
              onChange={e => setZusatz(e.target.value)}
              placeholder="z.B. 100% Haftung, Bitte mit 14-Tage-Frist"
              data-testid={`schriftsatz-zusatz-${typ}`}
            />
          </div>
        )}
      </div>

      {existing && !editing && (
        <SchriftsatzInlinePreview
          schriftsatz={existing}
          akte={akte}
          user={user}
          onEdit={() => setEditing(true)}
        />
      )}
      {existing && editing && (
        <SchriftsatzEditor
          schriftsatz={existing}
          akte={akte}
          user={user}
          onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); onSaved?.(); }}
        />
      )}

      {existing && onNext && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={onNext} data-testid={`schriftsatz-next-${typ}`}>
            Weiter <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}

function SchriftsatzInlinePreview({ schriftsatz, akte, user, onEdit }) {
  const { user: ctxUser } = useAuth();
  const u = user || ctxUser;
  return (
    <div className="bg-[#F3F4F6] rounded-lg p-3 overflow-auto" data-testid={`schriftsatz-preview-${schriftsatz.typ}`}>
      <A4Sheet
        content={schriftsatz.content}
        user={u}
        typ={schriftsatz.typ}
        createdAt={schriftsatz.created_at}
        actions={(
          <>
            {onEdit && (
              <Button variant="outline" size="sm" onClick={onEdit} data-testid={`schrift-edit-btn-${schriftsatz.typ}`}>
                <PencilSimple size={14} className="mr-1" /> Bearbeiten
              </Button>
            )}
            <WordButton schriftsatz={schriftsatz} />
          </>
        )}
        meta={{
          aktenzeichen: akte?.aktenzeichen,
          schadennr: akte?.gegner?.schadennr,
          empfaenger: akte?.gegner?.versicherung
            ? `${akte.gegner.versicherung}\n– Schadenabteilung –`
            : null,
        }}
      />
    </div>
  );
}

function VersandInlinePanel({ akte, schriftsaetze, onSaved }) {
  const offen = (schriftsaetze || []).filter(s => !s.versendet_am);
  const versendet = (schriftsaetze || []).filter(s => s.versendet_am);
  const [activeId, setActiveId] = useState(offen[0]?.id || null);
  const [form, setForm] = useState({ versendet_per: "post", empfaenger: "", notiz: "" });
  const [saving, setSaving] = useState(false);

  const TYP_LABEL = {
    anspruchsschreiben: "Anspruchsschreiben",
    klageschrift: "Klageschrift",
    rvg_honorar: "RVG-Honorarrechnung",
    owi_einspruch: "Einspruch (OWi)",
    bezifferungsschreiben: "Bezifferungsschreiben",
    unfallhergang: "Unfallhergang",
    vollmacht: "Vollmacht",
  };

  // Empfänger-Vorschlag basierend auf Schriftsatz-Typ
  useEffect(() => {
    const s = offen.find(x => x.id === activeId);
    if (!s) return;
    let empfaengerDefault = "";
    if (s.typ === "anspruchsschreiben" || s.typ === "klageschrift" || s.typ === "bezifferungsschreiben") {
      empfaengerDefault = akte?.gegner?.versicherung || akte?.gegner?.name || "";
    } else if (s.typ === "rvg_honorar") {
      empfaengerDefault = akte?.mandant_name || "";
    } else if (s.typ === "owi_einspruch") {
      empfaengerDefault = akte?.gegner?.name || "";
    }
    setForm(f => ({ ...f, empfaenger: f.empfaenger || empfaengerDefault }));
    // eslint-disable-next-line
  }, [activeId]);

  const markVersendet = async () => {
    if (!activeId) { toast.error("Schriftsatz wählen"); return; }
    setSaving(true);
    try {
      await api.post(`/schriftsaetze/${activeId}/versand`, form);
      toast.success("Versand protokolliert");
      setForm({ versendet_per: "post", empfaenger: "", notiz: "" });
      onSaved?.();
    } catch (err) { toast.error(errMsg(err, "Versand konnte nicht protokolliert werden")); } finally { setSaving(false); }
  };

  const resetVersand = async (sid) => {
    if (!window.confirm("Versand-Status zurücksetzen?")) return;
    try {
      await api.post(`/schriftsaetze/${sid}/versand-zuruecksetzen`);
      toast.success("Zurückgesetzt");
      onSaved?.();
    } catch (err) { toast.error(errMsg(err, "Zurücksetzen fehlgeschlagen")); }
  };

  return (
    <div className="space-y-4" data-testid="versand-inline-panel">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-5">
        <div>
          <p className="overline text-[#D32F2F] mb-1">Versand</p>
          <h2 className="font-serif text-2xl">Schreiben absenden</h2>
          <p className="text-sm text-[#6B7280] mt-1">Versandart erfassen — Status der Akte wird automatisch aktualisiert.</p>
        </div>

        {offen.length === 0 ? (
          <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded p-4 text-sm text-[#15803D]" data-testid="versand-empty">
            Alle Schriftsätze versendet.
          </div>
        ) : (
          <>
            <div>
              <Label className="text-xs text-[#6B7280]">Welcher Schriftsatz?</Label>
              <Select value={activeId || ""} onValueChange={setActiveId}>
                <SelectTrigger data-testid="versand-schriftsatz-select"><SelectValue placeholder="Schriftsatz wählen …" /></SelectTrigger>
                <SelectContent>
                  {offen.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {TYP_LABEL[s.typ] || s.typ} · {new Date(s.created_at).toLocaleDateString("de-DE")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B7280]">Versandart</Label>
                <Select value={form.versendet_per} onValueChange={v => setForm(f => ({ ...f, versendet_per: v }))}>
                  <SelectTrigger data-testid="versand-art-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="post">Post</SelectItem>
                    <SelectItem value="email">E-Mail</SelectItem>
                    <SelectItem value="bea">beA</SelectItem>
                    <SelectItem value="fax">Fax</SelectItem>
                    <SelectItem value="persönlich">Persönlich übergeben</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Empfänger</Label>
                <Input
                  value={form.empfaenger}
                  onChange={e => setForm(f => ({ ...f, empfaenger: e.target.value }))}
                  placeholder="z.B. HUK-Coburg, Schadenabteilung"
                  data-testid="versand-empfaenger"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-[#6B7280]">Notiz (optional)</Label>
                <Input
                  value={form.notiz}
                  onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))}
                  placeholder="z.B. Einschreiben mit Rückschein"
                  data-testid="versand-notiz"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={markVersendet} disabled={saving || !activeId} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="versand-mark-btn">
                {saving && <Spinner size={14} className="mr-2 animate-spin" />}
                <PaperPlaneTilt size={14} className="mr-2" weight="duotone" />
                Als versendet markieren
              </Button>
            </div>
          </>
        )}
      </div>

      {versendet.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="versand-history">
          <p className="overline text-[#9CA3AF] mb-3">Versendet ({versendet.length})</p>
          <ul className="divide-y divide-[#F3F4F6]">
            {versendet.map(s => (
              <li key={s.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#0A192F] truncate">
                    <CheckCircle size={14} weight="fill" className="inline-block text-[#16A34A] mr-1.5" />
                    {TYP_LABEL[s.typ] || s.typ}
                  </p>
                  <p className="text-[11px] text-[#6B7280] mt-0.5">
                    {new Date(s.versendet_am).toLocaleDateString("de-DE")} · {s.versendet_per || "—"} · {s.empfaenger || "—"}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => resetVersand(s.id)} data-testid={`versand-reset-${s.id}`}>
                  Zurücksetzen
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

